<script lang="ts">
  import * as Dialog from "$lib/components/ui/dialog/index.js";
  import { Button, buttonVariants } from "$lib/components/ui/button/index.js";
  import { Input } from "$lib/components/ui/input/index.js";
  import { Label } from "$lib/components/ui/label/index.js";
  import { t } from "svelte-i18n";
  import SelectCustomerType from "./select-customer-type.svelte";
  import * as zod from "zod";
  import { createForm } from "felte";
  import { validator } from "@felte/validator-zod";
  import { type AddressValue as AdrValue } from "$src/domain/core/AddressValue";
  import AddressValue from "$src/presentation/core/address-value.svelte";
  import { ScrollArea } from "$lib/components/ui/scroll-area/index.js";
  import { cn } from "$lib/utils";
  import { useCreateCustomer } from "../hooks/useCreateCustomer";
  import { createEventDispatcher, onMount, tick } from "svelte";
  import ErrorDialog from "$src/presentation/defaults/ErrorDialog.svelte";
  import { fetchCustomer } from "../hooks/useCustomer";
  import { toast } from "svelte-sonner";
  import { customerSchema } from "$src/domain/customers/Customer";
  import ZodTranslatedMessage from "$src/presentation/core/ZodTranslatedMessage.svelte";

  export let groupStoreId: number;
  export let defaultCountryCode: string | undefined = undefined;

  let isOpen: boolean = false;

  const mutation = useCreateCustomer();

  const dispatch = createEventDispatcher();

  onMount(() => {
    tick().then(() => (billingAddress.countryCode = defaultCountryCode));
  });

  let billingAddress: AdrValue = {
    countryCode: defaultCountryCode,
  };

  type FormValues = zod.infer<typeof customerSchema>;

  $: editForm = createForm({
    initialValues: {},
    onSubmit: handleSubmit,
    extend: validator({ schema: customerSchema }),
  });

  $: form = editForm.form;
  $: data = editForm.data;
  $: errors = editForm.errors;

  let lastnameInput: Input;

  async function handleSubmit(values: FormValues) {
    var customerIdentifier = await $mutation.mutateAsync({
      groupStoreId: groupStoreId,
      data: {
        customerTypeIdentifier: values.customerType.id,
        firstname: values.firstname ?? undefined,
        lastname: values.lastname ?? undefined,
        companyName: values.companyName ?? undefined,
        vatNumber: values.vatNumber ?? undefined,
        email: values.email,
        phoneNumber1: values.phoneNumber1,
        billingAddress: billingAddress,
      },
    });

    var customer = await fetchCustomer(groupStoreId, customerIdentifier.identifier);

    dispatch('customerCreated', customer);

    isOpen = false;

    toast($t('page.customers.new.created'));
  }
</script>

<Dialog.Root bind:open={isOpen}>
  <Dialog.Trigger class={buttonVariants({ variant: "outline" })}>
    {$t("page.customers.new.button")}
  </Dialog.Trigger>
  <Dialog.Content
    class="flex flex-col h-dvh sm:h-[650px] sm:my-auto overflow-hidden sm:max-h-dvh"
  >
    <Dialog.Header>
      <Dialog.Title>{$t("page.customers.new.title")}</Dialog.Title>
    </Dialog.Header>
    <form use:form class="flex flex-col justify-between h-full overflow-hidden">
      <div class="flex flex-col flex-grow h-full overflow-hidden">
        <ScrollArea orientation="vertical">
          <div class="grid w-full h-full grid-cols-2 gap-2 max-w-md p-2">
            <div class="col-span-full flex  gap-2 items-center flex-wrap">
              <Label
                for="customerType"
                class="text-right text-xs text-muted-foreground"
              >
                {$t("page.customers.labels.type")}
              </Label>
              <SelectCustomerType
                {groupStoreId}
                bind:value={$data.customerType}
                class={$errors.customerType ? "border-red-500" : ""}
              />
              {#if $errors.customerType}
                <div class="text-xs text-red-500 text-right w-full">
                  {$t("page.customers.labels.customerTypeRequired")}
                </div>
              {/if}
            </div>
            {#if $data.customerType && $data.customerType.id == 1}
              <div class="col-span-full flex items-center flex-wrap">
                <Label class="text-xs text-muted-foreground">
                  {$t("page.customers.labels.lastname")}
                </Label>
                <Input
                  bind:this={lastnameInput}
                  name="lastname"
                  class={cn(
                    "col-span-3",
                    $errors.lastname ? "border-red-500" : ""
                  )}
                />
                {#if $errors.lastname}
                  <div class="text-xs text-red-500 w-full text-right">
                    <ZodTranslatedMessage messages={$errors.lastname} />
                  </div>
                {/if}
              </div>
              <div class="col-span-full flex items-center flex-wrap">
                <Label for="firstname" class="text-xs text-muted-foreground">
                  {$t("page.customers.labels.firstname")}
                </Label>
                <Input
                  name="firstname"
                  bind:value={$data.firstname}
                  class={cn(
                    "col-span-3",
                    $errors.firstname ? "border-red-500" : ""
                  )}
                />
                {#if $errors.firstname}
                  <div class="text-xs text-red-500 w-full text-right">
                    <ZodTranslatedMessage messages={$errors.firstname} />
                  </div>
                {/if}
              </div>
            {:else if $data.customerType && $data.customerType.id > 1}
              <div class="col-span-full flex items-center flex-wrap">
                <Label for="companyName" class="text-xs text-muted-foreground">
                  {$t("page.customers.labels.companyName")}
                </Label>
                <Input
                  name="companyName"
                  bind:value={$data.companyName}
                  class={cn(
                    "col-span-3",
                    $errors.companyName ? "border-red-500" : ""
                  )}
                />
                {#if $errors.companyName}
                  <div class="text-xs text-red-500 w-full text-right">
                    <ZodTranslatedMessage messages={$errors.companyName} />
                  </div>
                {/if}
              </div>
              <div class="col-span-full flex items-center flex-wrap">
                <Label for="vatNumber" class="text-xs text-muted-foreground">
                  {$t("page.customers.labels.vatNumber")}
                </Label>
                <Input
                  name="vatNumber"
                  bind:value={$data.vatNumber}
                  class={cn(
                    "col-span-3",
                    $errors.vatNumber ? "border-red-500" : ""
                  )}
                />
                {#if $errors.vatNumber}
                  <div class="text-xs text-red-500 w-full text-right">
                    <ZodTranslatedMessage messages={$errors.vatNumber} />
                  </div>
                {/if}
              </div>
            {/if}
            <div class="col-span-full flex items-center flex-wrap">
              <AddressValue bind:value={billingAddress} editMode={true} />
            </div>
            <div class="col-span-full flex items-center flex-wrap">
              <Label for="phoneNumber1" class="text-xs text-muted-foreground">
                {$t("page.customers.labels.phoneNumber1")}
              </Label>
              <Input
                name="phoneNumber1"
                type="phone"
                bind:value={$data.phoneNumber1}
                class={cn(
                  "col-span-3",
                  $errors.phoneNumber1 ? "border-red-500" : ""
                )}
              />
              {#if $errors.phoneNumber1}
                <div class="text-xs text-red-500 w-full text-right">
                  <ZodTranslatedMessage messages={$errors.phoneNumber1} />
                </div>
              {/if}
            </div>
            <div class="col-span-full flex items-center flex-wrap">
              <Label for="email" class="text-xs text-muted-foreground">
                {$t("page.customers.labels.email")}
              </Label>
              <Input
                name="email"
                type="email"
                bind:value={$data.email}
                class={cn("col-span-3", $errors.email ? "border-red-500" : "")}
              />
              {#if $errors.email}
                <div class="text-xs text-red-500 w-full text-right">
                  <ZodTranslatedMessage messages={$errors.email} />
                </div>
              {/if}
            </div>
          </div>
        </ScrollArea>
      </div>
      <Dialog.Footer class="flex pt-4">
        <Button type="submit" class="px-8">
          {$t("global.messages.create")}
        </Button>
        {#if $mutation.isError } 
          <ErrorDialog error={$mutation.error} on:close={() => $mutation.reset()} />
        {/if}
      </Dialog.Footer>
    </form>
  </Dialog.Content>
</Dialog.Root>
